import { ChangeEvent, GALLERY_CHANGE_EVENT } from "./gallery";
import { MESSAGE_HANDLER_EVENT } from "./messagehandler";
import {
  SHORT_TEXT_GALLERY_CHANGE_EVENT,
  ShortTextGalleryChangeEvent,
} from "./shorttextgallery";
import { getViewport } from "./utils";

type tenant = "capital" | "geo" | "geolino" | "stern";

export const scriptUrl = {
  capital: {
    bottom: {
      live: "/capital.de/MoltenBundle.bottom.js",
      preview:
        "https://adctrl.emsmobile.de/molten/preview/capital/MoltenBundle.bottom.js",
    },
    top: {
      live: "/capital.de/MoltenBundle.top.js",
      preview:
        "https://adctrl.emsmobile.de/molten/preview/capital/MoltenBundle.top.js",
    },
  },
  geo: {
    bottom: {
      live: "/geo.de/MoltenBundle.bottom.js",
      preview:
        "https://adctrl.emsmobile.de/molten/preview/geo/MoltenBundle.bottom.js",
    },
    top: {
      live: "/geo.de/MoltenBundle.top.js",
      preview:
        "https://adctrl.emsmobile.de/molten/preview/geo/MoltenBundle.top.js",
    },
  },
  geolino: {
    bottom: {
      live: "/geolino.de/MoltenBundle.bottom.js",
      preview:
        "https://adctrl.emsmobile.de/molten/preview/geolino/MoltenBundle.bottom.js",
    },
    top: {
      live: "/geolino.de/MoltenBundle.top.js",
      preview:
        "https://adctrl.emsmobile.de/molten/preview/geolino/MoltenBundle.top.js",
    },
  },
  stern: {
    bottom: {
      live: "/stern.de/MoltenBundle.bottom.js",
      preview:
        "https://adctrl.emsmobile.de/molten/preview/stern/MoltenBundle.bottom.js",
    },
    top: {
      live: "/stern.de/MoltenBundle.top.js",
      preview:
        "https://adctrl.emsmobile.de/molten/preview/stern/MoltenBundle.top.js",
    },
  },
};

/**
 * Advertising library from the AdAlliance, named MoltenBundle
 * @see https://help.adalliance.io/home/tagcontrol/
 */
export class AdScript extends HTMLElement {
  #tenantNormalized: tenant;

  get tenant(): string {
    return this.getAttribute("scope") ?? "";
  }

  get contentType(): string {
    return this.getAttribute("content-type") ?? "";
  }

  get isIndexPage(): boolean {
    return this.getAttribute("is-index-page") === "true";
  }

  get keywords(): string[] {
    return JSON.parse(this.getAttribute("keywords") ?? "[]");
  }

  get name(): string {
    return this.getAttribute("name") ?? "";
  }

  get zone(): string {
    return this.getAttribute("zone") ?? "";
  }

  get userType(): string {
    return this.getAttribute("user-type") ?? "";
  }

  constructor() {
    super();
    this.#tenantNormalized = this.#setTenant();
    this.attachShadow({ mode: "open" });
  }

  #executeBottomScripts() {
    this.#loadScript(
      this.#isPreview()
        ? scriptUrl[this.#tenantNormalized].bottom.preview
        : scriptUrl[this.#tenantNormalized].bottom.live,
    );
    if (!Array.isArray(window.MoltenBundle?.cmd)) return;
    window.MoltenBundle.cmd.push(() => {
      window.MoltenBundle.finalize();
    });
    this.#addCustomEventListeners(
      "ws-gallery",
      GALLERY_CHANGE_EVENT,
      this.#handleGalleryChange,
    );
    this.#addCustomEventListeners(
      "ws-short-text-gallery",
      SHORT_TEXT_GALLERY_CHANGE_EVENT,
      this.#handleShortTextGalleryChange,
    );
    this.#addCustomEventListeners(
      "ws-messagehandler",
      MESSAGE_HANDLER_EVENT,
      this.#handleMessageHandlerEvent,
    );
  }

  #executeScripts() {
    switch (this.name) {
      case "bottom":
        this.#executeBottomScripts();
        break;
      case "top":
        this.#executeTopScripts();
        break;
      default:
        console.log(`Unknown adscript name: ${this.name}`);
        break;
    }
  }

  #resolveWidescreenViewportAsDesktop = (getViewportReturn: string): string => {
    if (getViewportReturn === "widescreen") {
      return "desktop";
    } else {
      return getViewportReturn;
    }
  };

  #executeTopScripts() {
    this.#loadScript(
      this.#isPreview()
        ? scriptUrl[this.#tenantNormalized].top.preview
        : scriptUrl[this.#tenantNormalized].top.live,
    );
    window.MoltenBundle = window.MoltenBundle || {};
    window.MoltenBundle.cmd = window.MoltenBundle.cmd || [];
    window.MoltenBundle.cmd.push(() => {
      window.MoltenBundle.getClient().setSiteType(
        this.#resolveWidescreenViewportAsDesktop(getViewport()),
      );
      window.MoltenBundle.getClient().setZone(this.zone);
      window.MoltenBundle.getClient().setIsIndexPage(this.isIndexPage);
      window.MoltenBundle.getClient().setKeywords(this.keywords);
      window.MoltenBundle.getClient().addTargetingParameter(
        "ct",
        this.contentType,
      );
      if (this.userType && this.userType === "plus") {
        window.MoltenBundle.getClient().addTargetingParameter("ut", "plus");
      }
    });
  }

  #addCustomEventListeners(
    elementName: string,
    eventName: string,
    handler: EventListener,
  ) {
    const elements = document.querySelectorAll(elementName);
    for (const element of elements) {
      element.addEventListener(eventName, handler);
    }
  }

  #handleGalleryChange = (event: Event) =>
    this.#handleGalleryEvent(event as ChangeEvent);

  #handleShortTextGalleryChange = (event: Event) =>
    this.#handleGalleryEvent(event as ShortTextGalleryChangeEvent);

  #handleGalleryEvent(event: {
    detail: { adTagIds?: string[]; itemNumber?: string };
  }) {
    const { adTagIds, itemNumber } = event.detail;
    const itemNumberAsNumber =
      itemNumber !== undefined ? parseInt(itemNumber) : undefined;
    if (
      typeof itemNumberAsNumber === "number" &&
      itemNumberAsNumber % 2 === 0
    ) {
      this.#reloadAds(adTagIds);
    }
  }

  #handleMessageHandlerEvent = (event: Event) => {
    const { detail } = event as CustomEvent;
    this.#reloadAds(detail.adTagIds);
  };

  #reloadAds(adTagIds?: string[]) {
    window.MoltenBundle.cmd.push(() => {
      window.MoltenBundle.getClient().reloadSpecificAds(adTagIds);
    });
  }

  #isPreview(): boolean {
    return window.location.search.includes("ada=1");
  }

  #loadScript(src: string) {
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    this.shadowRoot!.appendChild(script);
  }

  connectedCallback() {
    this.#executeScripts();
  }

  disconnectedCallback() {
    this.#removeCustomEventListeners(
      "ws-gallery",
      GALLERY_CHANGE_EVENT,
      this.#handleGalleryChange,
    );
    this.#removeCustomEventListeners(
      "ws-short-text-gallery",
      SHORT_TEXT_GALLERY_CHANGE_EVENT,
      this.#handleShortTextGalleryChange,
    );
  }

  #setTenant(): tenant {
    if (this.tenant === "capital") {
      return "capital";
    }
    if (this.tenant === "geo") {
      return "geo";
    } else if (this.tenant === "geolino") {
      return "geolino";
    }

    return "stern";
  }

  #removeCustomEventListeners(
    elementName: string,
    eventName: string,
    handler: EventListener,
  ) {
    const elements = document.querySelectorAll(elementName);
    for (const element of elements) {
      element.removeEventListener(eventName, handler);
    }
  }
}

"customElements" in window &&
  customElements.get("ws-adscript") === undefined &&
  customElements.define("ws-adscript", AdScript);

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface HTMLElementTagNameMap {
    "ws-adscript": AdScript;
  }
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    MoltenBundle: any;
  }
}
